import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import RichTextEditor from '../../components/editor/RichTextEditor';
import {
  INBOX_FORM_STATUS_OPTIONS,
  INBOX_STATUS,
  mergeInboxDetailsWithLanguages,
  stripInboxDetails,
} from './siteMessageConstants';

const EMPTY_FORM = {
  memberId: '',
  status: INBOX_STATUS.UNREAD,
  imgUrl: '',
  filmUrl: '',
  details: [],
};

export default function SiteMessageSingleEditModal({ open, inboxId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(inboxId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const langList = await adminApi.newsLanguages();
        if (cancelled) {
          return;
        }
        setLanguages(langList || []);
        if (inboxId) {
          const data = await adminApi.getInbox(inboxId);
          if (cancelled) {
            return;
          }
          setForm({
            memberId: data.memberId != null ? String(data.memberId) : '',
            status: data.status ?? INBOX_STATUS.UNREAD,
            imgUrl: data.imgUrl || '',
            filmUrl: data.filmUrl || '',
            details: mergeInboxDetailsWithLanguages(langList, data.details),
          });
        } else {
          setForm({
            ...EMPTY_FORM,
            details: mergeInboxDetailsWithLanguages(langList, []),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, inboxId]);

  function updateDetail(index, key, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], [key]: value };
      return { ...prev, details: nextDetails };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!form.memberId?.trim()) {
      setError('請輸入用戶ID');
      return;
    }
    const details = stripInboxDetails(form.details).filter((d) => d.title?.trim());
    if (!details.length) {
      setError('至少填寫一個語系的標題');
      return;
    }
    const payload = {
      memberId: Number(form.memberId),
      status: Number(form.status),
      imgUrl: form.imgUrl?.trim() || '',
      filmUrl: form.filmUrl?.trim() || '',
      details,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateInbox({ id: inboxId, ...payload });
      } else {
        await adminApi.createInbox(payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? `修改站內信 - ${inboxId}` : '新增站內信'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <p className="table-empty">載入中…</p>
        ) : (
          <form className="modal-panel__body site-message-edit-form" onSubmit={handleSubmit}>
            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <div className="form-field">
              <label htmlFor="inboxMemberId">
                <span className="form-label-required">用戶ID</span>
              </label>
              <input
                id="inboxMemberId"
                type="number"
                min="1"
                value={form.memberId}
                onChange={(e) => setForm((p) => ({ ...p, memberId: e.target.value }))}
                disabled={isEdit}
                required
              />
            </div>

            <div className="form-field">
              <span className="form-field__label">狀態</span>
              <div className="product-status-radios">
                {INBOX_FORM_STATUS_OPTIONS.map((option) => (
                  <label key={option.value} className="product-status-radios__item">
                    <input
                      type="radio"
                      name="inboxStatus"
                      checked={Number(form.status) === option.value}
                      onChange={() => setForm((p) => ({ ...p, status: option.value }))}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="inboxImgUrl">圖片連結</label>
              <input
                id="inboxImgUrl"
                type="text"
                value={form.imgUrl}
                onChange={(e) => setForm((p) => ({ ...p, imgUrl: e.target.value }))}
              />
            </div>

            <div className="form-field">
              <label htmlFor="inboxFilmUrl">影片連結</label>
              <input
                id="inboxFilmUrl"
                type="text"
                value={form.filmUrl}
                onChange={(e) => setForm((p) => ({ ...p, filmUrl: e.target.value }))}
                placeholder="請輸入影片連結（選填）"
              />
            </div>

            {form.details.map((detail, index) => (
              <section key={detail.lang} className="site-message-lang-section">
                <div className="form-field">
                  <label htmlFor={`inbox-title-${detail.lang}`}>
                    <span className="form-label-required">標題（{detail._label || detail.lang}）</span>
                  </label>
                  <input
                    id={`inbox-title-${detail.lang}`}
                    type="text"
                    value={detail.title || ''}
                    onChange={(e) => updateDetail(index, 'title', e.target.value)}
                  />
                </div>
                <div className="form-field form-field--full">
                  <span className="form-label">內容（{detail._label || detail.lang}）</span>
                  <RichTextEditor
                    value={detail.body || ''}
                    onChange={(value) => updateDetail(index, 'body', value)}
                  />
                </div>
              </section>
            ))}

            <div className="modal-panel__footer admin-modal__footer--center">
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? '提交中…' : '提交'}
              </button>
              <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
                關閉
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
